/* ==========================================================================
   Sisters Moto: collection / search filters (v12)
   Markup: snippets/facets.liquid  ·  Styles: assets/sm-collection-filters.css

   - Every change re-renders the section over AJAX (Section Rendering API) and
     updates the URL + history, like Dawn's FacetFiltersForm.
   - The grid (#ProductGridContainer) is replaced; assets/sm-group.js watches it.
   - The filter controls are PATCHED in place (checked / disabled / counts), so
     focus, open groups, list searches and "Show more" survive a re-render.
   - Desktop (>= 990px): sticky sidebar, applies instantly (price debounced 300ms).
   - Phones / tablets: the same panel opens as a full-screen dialog; changes apply
     live and the dialog stays open until "Show N results".
   ========================================================================== */

class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    if (this._smBound) return;
    this._smBound = true;
    const form = this.querySelector('form');
    if (!form) return;

    this.debouncedSubmit = () => {
      FacetFiltersForm.cancelPending();
      FacetFiltersForm.pendingTimer = setTimeout(() => {
        FacetFiltersForm.pendingTimer = null;
        this.onSubmitHandler();
      }, 300);
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onSubmitHandler();
    });

    form.addEventListener('change', (event) => {
      const t = event.target;
      if (!t.name) return; // list search boxes, price sliders
      if (t.name === 'sort_by') {
        if (t.value === 'availability') {
          this.sortGridByAvailability();
          return;
        }
        this.onSubmitHandler();
        return;
      }
      if (t.classList.contains('sm-price__input')) {
        this.debouncedSubmit();
        return;
      }
      FacetFiltersForm.refreshBadges();
      this.onSubmitHandler();
    });

    // Price fields apply while typing (debounced)
    form.addEventListener('input', (event) => {
      if (event.target.classList && event.target.classList.contains('sm-price__input')) this.debouncedSubmit();
    });
  }

  // A pending (debounced) price update is dropped when another change is applied first
  static cancelPending() {
    if (FacetFiltersForm.pendingTimer) clearTimeout(FacetFiltersForm.pendingTimer);
    FacetFiltersForm.pendingTimer = null;
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state && typeof event.state.searchParams === 'string'
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
      FacetFiltersForm.syncSort(searchParams);
    };
    window.addEventListener('popstate', onHistoryChange);

    // Links inside <facet-remove> (chips, Clear all, In stock toggle) render over AJAX
    document.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('facet-remove a[href]');
      if (!link || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      FacetFiltersForm.cancelPending();
      const href = link.getAttribute('href') || '';
      const q = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(q.replace(/^&+/, ''), event);
    });
  }

  static syncSort(searchParams) {
    const select = document.getElementById('SortBy');
    if (!select) return;
    const v = new URLSearchParams(searchParams).get('sort_by') || select.dataset.default || '';
    if (v && Array.prototype.some.call(select.options, (o) => o.value === v)) select.value = v;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.sm-filters').forEach((el) => el.classList.toggle('is-loading', disable));
  }

  static setLoading(on) {
    document.querySelectorAll('.sm-filters').forEach((el) => el.classList.toggle('is-loading', on));
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    const coll = grid.querySelector('.collection');
    if (coll) coll.classList.toggle('loading', on);
    if (on) grid.setAttribute('aria-busy', 'true');
    else grid.removeAttribute('aria-busy');
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) {
      window.location.search = searchParams;
      return;
    }
    const seq = ++FacetFiltersForm.seq;
    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}${searchParams ? '&' + searchParams : ''}`;
      const cached = FacetFiltersForm.filterData.find((element) => element.url === url);
      if (cached) {
        FacetFiltersForm.renderAll(cached.html, seq);
      } else {
        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(String(response.status));
            return response.text();
          })
          .then((html) => {
            FacetFiltersForm.filterData.push({ html, url });
            if (FacetFiltersForm.filterData.length > 24) FacetFiltersForm.filterData.shift();
            FacetFiltersForm.renderAll(html, seq);
          })
          .catch(() => {
            // Network / server problem: fall back to a normal page load with the same filters
            if (seq === FacetFiltersForm.seq) {
              window.location.href = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
            }
          });
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderAll(html, seq) {
    if (seq !== FacetFiltersForm.seq) return; // a newer request has started
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsed);
    FacetFiltersForm.renderProductGridContainer(parsed);
    FacetFiltersForm.renderProductCount(parsed);
    FacetFiltersForm.setLoading(false);
  }

  /* ---------- grid + counts ---------- */

  static renderProductGridContainer(parsed) {
    const src = parsed.getElementById('ProductGridContainer');
    const dest = document.getElementById('ProductGridContainer');
    if (src && dest) dest.innerHTML = src.innerHTML;

    // Re-apply the client-side availability sort if it is selected
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sort_by') === 'availability') {
      const form = document.querySelector('facet-filters-form');
      if (form) form.sortGridByAvailability(false);
    }
  }

  static renderProductCount(parsed) {
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const src = parsed.getElementById(id);
      const dest = document.getElementById(id);
      if (src && dest) dest.innerHTML = src.innerHTML;
    });
    const newRoot = parsed.querySelector('[data-sm-filters]');
    const root = document.querySelector('[data-sm-filters]');
    if (newRoot && root) {
      const n = parseInt(newRoot.getAttribute('data-sm-count'), 10) || 0;
      root.setAttribute('data-sm-count', String(n));
      root.setAttribute('data-sm-active', newRoot.getAttribute('data-sm-active') || '0');
      root.querySelectorAll('[data-sm-show]').forEach((btn) => {
        btn.textContent = FacetFiltersForm.showLabel(n);
      });
      const live = root.querySelector('[data-sm-live]');
      const countEl = document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
      if (live && countEl) {
        // Clear first so the same text is announced again
        live.textContent = '';
        setTimeout(() => { live.textContent = 'Filters updated: ' + countEl.textContent.trim(); }, 60);
      }
    }
  }

  static showLabel(n) {
    if (n === 0) return 'No results – change filters';
    return 'Show ' + n.toLocaleString('en-US') + (n === 1 ? ' result' : ' results');
  }

  /* ---------- filter controls ---------- */

  static renderFilters(parsed) {
    const newRoot = parsed.querySelector('[data-sm-filters]');
    const root = document.querySelector('[data-sm-filters]');
    if (!newRoot || !root) return;

    // Structure changed (e.g. filters appeared / disappeared): replace the whole block
    const hasPanel = !!root.querySelector('[data-sm-panel]');
    const newHasPanel = !!newRoot.querySelector('[data-sm-panel]');
    if (hasPanel !== newHasPanel) {
      if (window.SmFilters) window.SmFilters.close(true);
      root.outerHTML = newRoot.outerHTML;
      if (window.SmFilters) window.SmFilters.refresh();
      return;
    }

    // Simple regions: replace contents (keep focus near where it was in the chips row)
    root.querySelectorAll('[data-sm-region]').forEach((region) => {
      const name = region.getAttribute('data-sm-region');
      const src = newRoot.querySelector(`[data-sm-region="${name}"]`);
      if (!src) return;
      const active = document.activeElement;
      let focusIndex = -1;
      if (active && region.contains(active)) {
        focusIndex = Array.prototype.indexOf.call(region.querySelectorAll('a, button'), active);
      }
      region.innerHTML = src.innerHTML;
      region.hidden = src.hidden;
      if (focusIndex > -1) {
        const items = region.querySelectorAll('a, button');
        const next = items[Math.min(focusIndex, items.length - 1)];
        if (next) next.focus({ preventScroll: true });
        else FacetFiltersForm.focusFallback(region);
      }
    });

    // Groups: patch in place
    const body = root.querySelector('[data-sm-scroll]');
    const newBody = newRoot.querySelector('[data-sm-scroll]');
    if (!body || !newBody) return;
    const keysNow = Array.from(body.querySelectorAll('[data-sm-group]')).map((g) => g.getAttribute('data-sm-group'));
    const keysNew = Array.from(newBody.querySelectorAll('[data-sm-group]')).map((g) => g.getAttribute('data-sm-group'));
    if (keysNow.join('|') !== keysNew.join('|')) {
      FacetFiltersForm.replaceKeepingState(body, newBody);
      return;
    }
    newBody.querySelectorAll('[data-sm-group]').forEach((src) => {
      const key = src.getAttribute('data-sm-group');
      const dest = Array.from(body.querySelectorAll('[data-sm-group]')).find((g) => g.getAttribute('data-sm-group') === key);
      if (dest) FacetFiltersForm.patchGroup(dest, src);
    });
    if (window.SmFilters) window.SmFilters.refresh();
  }

  static focusFallback(region) {
    // Inside the open drawer: stay in it (panel title). Otherwise: the results count.
    const panel = region && region.closest('[data-sm-panel]');
    const count = document.getElementById('ProductCountDesktop');
    const target = panel ? panel.querySelector('.sm-filters__title') : count && count.closest('p');
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  static patchGroup(dest, src) {
    const ids = (el) => Array.from(el.querySelectorAll('input[id]')).map((i) => i.id).join('|');
    if (ids(dest) !== ids(src)) {
      // Different options: swap the contents but keep open state, search text and focus
      const wasOpen = dest.tagName === 'DETAILS' ? dest.open : null;
      const focusedId = dest.contains(document.activeElement) ? document.activeElement.id : null;
      const search = dest.querySelector('[data-sm-search]');
      const q = search ? search.value : '';
      const expanded = !!dest.querySelector('.sm-flist:not(.is-collapsed)');
      dest.innerHTML = src.innerHTML;
      if (wasOpen !== null) dest.open = wasOpen;
      const s2 = dest.querySelector('[data-sm-search]');
      if (s2 && q) s2.value = q;
      if (expanded) {
        const list = dest.querySelector('.sm-flist');
        const more = dest.querySelector('[data-sm-more]');
        if (list) list.classList.remove('is-collapsed');
        if (more) { more.setAttribute('aria-expanded', 'true'); more.textContent = 'Show fewer'; }
      }
      if (focusedId) {
        const el = document.getElementById(focusedId);
        if (el) el.focus({ preventScroll: true });
      }
      return;
    }

    src.querySelectorAll('input[id]').forEach((input) => {
      const cur = document.getElementById(input.id);
      if (!cur || !dest.contains(cur)) return;
      if (cur.type === 'checkbox') {
        cur.checked = input.hasAttribute('checked');
        cur.disabled = input.hasAttribute('disabled');
        const li = cur.closest('.sm-opt');
        const srcLi = input.closest('.sm-opt');
        if (li && srcLi) {
          li.classList.toggle('is-off', srcLi.classList.contains('is-off'));
          li.toggleAttribute('data-sm-extra', srcLi.hasAttribute('data-sm-extra'));
        }
      } else if (cur.type === 'number' && cur !== document.activeElement) {
        cur.value = input.getAttribute('value') || '';
        ['min', 'max', 'placeholder'].forEach((a) => { if (input.hasAttribute(a)) cur.setAttribute(a, input.getAttribute(a)); });
      }
    });
    // Counts in labels
    src.querySelectorAll('[data-sm-text]').forEach((label) => {
      const forId = label.getAttribute('for');
      const cur = forId && dest.querySelector(`label[for="${CSS.escape(forId)}"]`);
      if (cur && cur.innerHTML !== label.innerHTML) cur.innerHTML = label.innerHTML;
    });
    // Price slider bounds
    const srcRanges = src.querySelectorAll('[data-sm-range]');
    if (srcRanges.length) {
      const price = dest.querySelector('[data-sm-price]');
      const priceFocused = price && price.contains(document.activeElement);
      if (price && src.querySelector('[data-sm-price]')) {
        price.setAttribute('data-top', src.querySelector('[data-sm-price]').getAttribute('data-top'));
      }
      if (price && !priceFocused && price.syncFromFields) price.syncFromFields();
      else if (price && price.paint) price.paint();
      const note = dest.querySelector('.sm-price__note');
      const srcNote = src.querySelector('.sm-price__note');
      if (note && srcNote) note.innerHTML = srcNote.innerHTML;
    }
    // Group badge + "Show N more" label
    const badge = dest.querySelector('[data-sm-badge]');
    const srcBadge = src.querySelector('[data-sm-badge]');
    if (badge && srcBadge) {
      badge.innerHTML = srcBadge.innerHTML;
      badge.hidden = srcBadge.hidden;
    }
    const more = dest.querySelector('[data-sm-more]');
    const srcMore = src.querySelector('[data-sm-more]');
    if (more && srcMore) {
      more.setAttribute('data-more-label', srcMore.getAttribute('data-more-label'));
      if (more.getAttribute('aria-expanded') !== 'true') more.textContent = srcMore.getAttribute('data-more-label');
    }
  }

  static replaceKeepingState(body, newBody) {
    const open = {};
    body.querySelectorAll('details[data-sm-group]').forEach((d) => { open[d.getAttribute('data-sm-group')] = d.open; });
    const focusedId = body.contains(document.activeElement) ? document.activeElement.id : null;
    const scroll = body.scrollTop;
    body.innerHTML = newBody.innerHTML;
    body.querySelectorAll('details[data-sm-group]').forEach((d) => {
      const k = d.getAttribute('data-sm-group');
      if (k in open) d.open = open[k];
    });
    body.scrollTop = scroll;
    if (focusedId) {
      const el = document.getElementById(focusedId);
      if (el) el.focus({ preventScroll: true });
    }
    if (window.SmFilters) window.SmFilters.refresh();
  }

  static refreshBadges() {
    document.querySelectorAll('.sm-filters details[data-sm-group]').forEach((group) => {
      const badge = group.querySelector('[data-sm-badge]');
      if (!badge || group.classList.contains('sm-fgroup--price')) return;
      const n = group.querySelectorAll('input[type="checkbox"]:checked').length;
      badge.hidden = n === 0;
      if (badge.firstChild && badge.firstChild.nodeType === 3) badge.firstChild.nodeValue = String(n);
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return grid && grid.dataset.id ? [{ section: grid.dataset.id }] : [];
  }

  static createSearchParams(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value !== 'string') return;
      if (value === '' && key !== 'q') return; // empty price fields
      params.append(key, value);
    });
    // Min above max: swap them
    const gte = Array.from(params.keys()).find((k) => /\.gte$/.test(k));
    const lte = Array.from(params.keys()).find((k) => /\.lte$/.test(k));
    if (gte && lte && Number(params.get(gte)) > Number(params.get(lte))) {
      const a = params.get(gte);
      params.set(gte, params.get(lte));
      params.set(lte, a);
    }
    return params.toString();
  }

  onSubmitHandler() {
    const form = this.querySelector('form');
    if (!form) return;
    FacetFiltersForm.cancelPending();
    const params = FacetFiltersForm.createSearchParams(form);
    if (params === FacetFiltersForm.searchParamsPrev) return; // nothing changed (e.g. blur after typing)
    FacetFiltersForm.renderPage(params);
  }

  sortGridByAvailability(push = true) {
    let grid = document.getElementById('product-grid');
    // Search results: #product-grid is a wrapper, the cards are in the <ul> inside it
    if (grid && grid.tagName !== 'UL') grid = grid.querySelector('ul.product-grid');
    if (!grid) return;
    const items = Array.from(grid.children);
    items.sort((a, b) => {
      const aAvailable = a.getAttribute('data-available') === 'true';
      const bAvailable = b.getAttribute('data-available') === 'true';
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
      return 0;
    });
    items.forEach((item) => grid.appendChild(item));
    if (!push) return;
    const url = new URL(window.location);
    url.searchParams.set('sort_by', 'availability');
    FacetFiltersForm.searchParamsPrev = url.search.slice(1);
    window.history.pushState({ searchParams: url.search.slice(1) }, '', url);
  }

  // Kept for compatibility with older callers
  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget.href || '';
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.seq = 0;
FacetFiltersForm.pendingTimer = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

/* ---------- price: dual slider synced with the Min / Max fields ---------- */
class PriceRange extends HTMLElement {
  connectedCallback() {
    if (this._smBound) return;
    this._smBound = true;
    this.addEventListener('input', (event) => {
      const t = event.target;
      if (t.matches('[data-sm-range]')) {
        this.onSlide(t);
      } else if (t.matches('.sm-price__input')) {
        this.syncFromFields();
      }
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-sm-range]')) {
        // Hand over to the form: a change on the Max field applies the filter (debounced)
        const max = this.querySelectorAll('.sm-price__input')[1];
        if (max) max.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this.syncFromFields();
  }

  // Slider position (0-200) <-> price, square-root scale
  toPrice(pos) {
    const top = this.top;
    const raw = top * Math.pow(Number(pos) / 200, 2);
    const step = raw < 100 ? 1 : raw < 500 ? 5 : raw < 2000 ? 10 : 50;
    return Math.min(top, Math.round(raw / step) * step);
  }

  toPos(price) {
    const top = this.top || 1;
    return Math.round(Math.sqrt(Math.min(Math.max(price, 0), top) / top) * 200);
  }

  get parts() {
    const ranges = this.querySelectorAll('[data-sm-range]');
    const fields = this.querySelectorAll('.sm-price__input');
    return { lo: ranges[0], hi: ranges[1], min: fields[0], max: fields[1], fill: this.querySelector('.sm-price__fill') };
  }

  get top() {
    return Number(this.getAttribute('data-top')) || 0;
  }

  onSlide(r) {
    const p = this.parts;
    if (!p.lo || !p.hi) return;
    const gap = 2;
    if (r === p.lo && Number(p.lo.value) > Number(p.hi.value) - gap) p.lo.value = Math.max(0, Number(p.hi.value) - gap);
    if (r === p.hi && Number(p.hi.value) < Number(p.lo.value) + gap) p.hi.value = Math.min(200, Number(p.lo.value) + gap);
    // Full range = no price filter, so leave the fields empty instead of 0 / top
    const a = Number(p.lo.value) > 0 ? this.toPrice(p.lo.value) : 0;
    const b = Number(p.hi.value) < 200 ? this.toPrice(p.hi.value) : this.top;
    p.min.value = a > 0 ? String(a) : '';
    p.max.value = b < this.top ? String(b) : '';
    this.paint();
  }

  syncFromFields() {
    const p = this.parts;
    if (!p.lo || !p.hi) return;
    const a = parseFloat(p.min.value);
    const b = parseFloat(p.max.value);
    p.lo.value = isNaN(a) ? 0 : this.toPos(a);
    p.hi.value = isNaN(b) ? 200 : this.toPos(b);
    this.paint();
  }

  paint() {
    const p = this.parts;
    if (!p.lo || !p.hi || !p.fill) return;
    const a = Math.min(Number(p.lo.value), Number(p.hi.value));
    const b = Math.max(Number(p.lo.value), Number(p.hi.value));
    p.fill.style.left = a / 2 + '%';
    p.fill.style.right = 100 - b / 2 + '%';
    const cur = this.getAttribute('data-currency') || '$';
    const lo = parseFloat(p.min.value);
    const hi = parseFloat(p.max.value);
    p.lo.setAttribute('aria-valuetext', cur + (isNaN(lo) ? 0 : lo).toLocaleString('en-US'));
    p.hi.setAttribute('aria-valuetext', isNaN(hi) ? cur + this.top.toLocaleString('en-US') + ' or more' : cur + hi.toLocaleString('en-US'));
    // keep the handle you can still move on top when both meet at the right end
    p.lo.style.zIndex = a > 180 ? 3 : 2;
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

/* Links in <facet-remove> are handled by one delegated listener (see setListeners) */
class FacetRemove extends HTMLElement {}
if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);

/* ==========================================================================
   Panel behaviour: drawer dialog on small screens, list search, "Show more"
   ========================================================================== */
(function () {
  'use strict';

  var desktop = window.matchMedia('(min-width: 990px)');
  var lastFocus = null;

  function root() { return document.querySelector('[data-sm-filters]'); }
  function panel() { var r = root(); return r && r.querySelector('[data-sm-panel]'); }
  function isSideMode() {
    var r = root();
    return !!(r && r.classList.contains('sm-filters--side') && desktop.matches);
  }

  /* ---------- drawer ---------- */
  function focusables(el) {
    return Array.prototype.filter.call(
      el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null || n === document.activeElement; }
    );
  }

  function open() {
    var p = panel();
    if (!p || isSideMode()) return;
    lastFocus = document.activeElement;
    p.classList.add('is-open');
    p.setAttribute('role', 'dialog');
    p.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add('sm-filters-lock');
    document.querySelectorAll('[data-sm-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var close = p.querySelector('.sm-filters__close');
    setTimeout(function () { (close || p).focus({ preventScroll: true }); }, 30);
  }

  function close(silent) {
    var p = panel();
    if (!p || !p.classList.contains('is-open')) return;
    p.classList.remove('is-open');
    p.setAttribute('role', 'region');
    p.removeAttribute('aria-modal');
    document.documentElement.classList.remove('sm-filters-lock');
    document.querySelectorAll('[data-sm-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (silent) return;
    var opener = document.querySelector('[data-sm-open]');
    var target = lastFocus && lastFocus.isConnected && !p.contains(lastFocus) ? lastFocus : opener;
    if (target) target.focus({ preventScroll: true });
    // Bring the results into view on phones
    var grid = document.getElementById('ProductGridContainer');
    var bar = document.querySelector('[data-sm-bar]');
    if (grid && bar && bar.getBoundingClientRect().top < 0) bar.scrollIntoView({ block: 'start' });
  }

  // Tablet drawer: a click on the dimmed page closes the drawer instead of reaching the page
  document.addEventListener('click', function (e) {
    var p = panel();
    if (!p || !p.classList.contains('is-open') || !e.target.closest) return;
    if (p.contains(e.target) || e.target.closest('[data-sm-open]')) return;
    e.preventDefault();
    e.stopPropagation();
    close();
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;
    if (t.closest('[data-sm-open]')) { e.preventDefault(); open(); return; }
    if (t.closest('[data-sm-close]')) { e.preventDefault(); close(); return; }
    var more = t.closest('[data-sm-more]');
    if (more) {
      var list = document.getElementById(more.getAttribute('aria-controls'));
      if (!list) return;
      var collapsed = list.classList.toggle('is-collapsed');
      more.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      more.textContent = collapsed ? more.getAttribute('data-more-label') : 'Show fewer';
    }
  });

  document.addEventListener('keydown', function (e) {
    var p = panel();
    // Enter in a list search box would submit the form (full reload)
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-sm-search]')) { e.preventDefault(); return; }
    if (!p || !p.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      if (e.target.matches && e.target.matches('[data-sm-search]') && e.target.value) return; // clears the search first
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusables(p);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !p.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !p.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
  });

  function onMode() {
    if (isSideMode()) close(true);
  }
  if (desktop.addEventListener) desktop.addEventListener('change', onMode);
  else if (desktop.addListener) desktop.addListener(onMode);

  /* ---------- search inside long lists ---------- */
  function runSearch(input) {
    var body = input.closest('.sm-fgroup__body');
    var list = body && body.querySelector('.sm-flist');
    if (!list) return;
    var q = input.value.trim().toLowerCase();
    var shown = 0;
    list.querySelectorAll('li[data-sm-label]').forEach(function (li) {
      var hit = !q || li.getAttribute('data-sm-label').indexOf(q) !== -1;
      li.hidden = !hit;
      if (hit) shown++;
    });
    list.classList.toggle('is-searching', !!q);
    var empty = body.querySelector('[data-sm-empty]');
    if (empty) empty.hidden = shown !== 0;
    var more = body.querySelector('[data-sm-more]');
    if (more) more.hidden = !!q;
  }

  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('.sm-filters [data-sm-search]')) runSearch(e.target);
  });

  function refresh() {
    document.querySelectorAll('.sm-filters [data-sm-search]').forEach(function (input) {
      if (input.value) runSearch(input);
    });
    document.querySelectorAll('.sm-filters price-range').forEach(function (pr) { if (pr.paint) pr.paint(); });
  }

  window.SmFilters = { refresh: refresh, open: open, close: close };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
